import { Table } from '@tanstack/react-table';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useRef } from 'react';
import { Button } from '../button/button';
import { scrollToTop } from '../data-table/utils/scroll-to-top';
import { Inline } from '../inline/inline';

type PaginationNewProps<TData> = {
  table: Table<TData>;
  className?: string;
};

/**
 * Pagination controls for the data table.
 * Purely presentational - consumer controls visibility.
 *
 * @param props.table - TanStack Table instance
 * @param props.className - Additional classes for the pagination container
 *
 * @example
 * ```tsx
 * const table = useReactTable({
 *   data,
 *   columns,
 *   getPaginationRowModel: getPaginationRowModel(),
 *   state: { pagination: { pageIndex: 0, pageSize: 20 } }
 * });
 *
 * // Consumer controls when to show pagination
 * {table.getPageCount() > 1 && (
 *   <DataTableNew.Pagination table={table} />
 * )}
 * ```
 */
export const PaginationNew = <TData,>({ table, className }: PaginationNewProps<TData>) => {
  const paginationRef = useRef<HTMLDivElement>(null);

  return (
    <div ref={paginationRef} className={className}>
      <Inline space={4} align="center" justify="end">
        <Inline space={0} align="center" justify="center" className="w-[100px] text-xs font-medium">
          Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
        </Inline>

        <Button
          type="button"
          variant="outline"
          className="w-8 px-0"
          onClick={() => {
            table.setPageIndex(0);
            scrollToTop(paginationRef.current);
          }}
          disabled={!table.getCanPreviousPage()}
          aria-label="Go to first page"
        >
          <ChevronsLeft />
        </Button>

        <Button
          type="button"
          variant="outline"
          className="w-8 px-0"
          onClick={() => {
            table.previousPage();
            scrollToTop(paginationRef.current);
          }}
          disabled={!table.getCanPreviousPage()}
          aria-label="Go to previous page"
        >
          <ChevronLeft />
        </Button>

        <Button
          type="button"
          variant="outline"
          className="w-8 px-0"
          onClick={() => {
            table.nextPage();
            scrollToTop(paginationRef.current);
          }}
          disabled={!table.getCanNextPage()}
          aria-label="Go to next page"
        >
          <ChevronRight />
        </Button>

        <Button
          type="button"
          variant="outline"
          className="w-8 px-0"
          onClick={() => {
            table.setPageIndex(table.getPageCount() - 1);
            scrollToTop(paginationRef.current);
          }}
          disabled={!table.getCanNextPage()}
          aria-label="Go to last page"
        >
          <ChevronsRight />
        </Button>
      </Inline>
    </div>
  );
};
